import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
	AuthState,
	LoginResult,
	User,
	UserRole,
	USER_ROLE,
	UNSUPPORTED_API_ROLES,
} from '../types/auth';
import { authApi, ApiError, STORAGE_KEYS, SESSION_EXPIRATION_MS } from '../api';

// Validate if the API role is supported in the business portal
const isValidUserRole = (apiRole?: string): apiRole is UserRole => {
	if (!apiRole) return false;

	// Check if role is unsupported (e.g., ROLE_CUSTOMER)
	if (UNSUPPORTED_API_ROLES.includes(apiRole as (typeof UNSUPPORTED_API_ROLES)[number])) {
		return false;
	}

	// Check if role matches any of our supported USER_ROLE values
	const supportedRoles = Object.values(USER_ROLE) as string[];
	return supportedRoles.includes(apiRole);
};

// Helper to get storage based on remember me preference
const getStorage = () => {
	const rememberMe = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
	return rememberMe ? localStorage : sessionStorage;
};

// Helper to save auth data to storage
const saveAuthToStorage = (token: string, user: User, rememberMe: boolean) => {
	const storage = rememberMe ? localStorage : sessionStorage;
	const loginTimestamp = Date.now().toString();

	// Save remember me preference to localStorage (always)
	localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, String(rememberMe));

	// Save token, user, and login timestamp to appropriate storage
	storage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
	storage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
	storage.setItem(STORAGE_KEYS.LOGIN_TIMESTAMP, loginTimestamp);

	// If using sessionStorage, clear localStorage auth data
	if (!rememberMe) {
		localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
		localStorage.removeItem(STORAGE_KEYS.USER_DATA);
		localStorage.removeItem(STORAGE_KEYS.LOGIN_TIMESTAMP);
	}
};

// Helper to clear auth data from both storages
const clearAuthFromStorage = () => {
	localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
	localStorage.removeItem(STORAGE_KEYS.USER_DATA);
	localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
	localStorage.removeItem(STORAGE_KEYS.LOGIN_TIMESTAMP);
	sessionStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
	sessionStorage.removeItem(STORAGE_KEYS.USER_DATA);
	sessionStorage.removeItem(STORAGE_KEYS.LOGIN_TIMESTAMP);
};

// Helper to check if session has expired
const isSessionExpired = (): boolean => {
	// Check localStorage first, then sessionStorage
	let loginTimestamp = localStorage.getItem(STORAGE_KEYS.LOGIN_TIMESTAMP);

	if (!loginTimestamp) {
		loginTimestamp = sessionStorage.getItem(STORAGE_KEYS.LOGIN_TIMESTAMP);
	}

	if (!loginTimestamp) {
		return true; // No timestamp means no valid session
	}

	const loginTime = parseInt(loginTimestamp, 10);
	const currentTime = Date.now();
	const elapsed = currentTime - loginTime;

	return elapsed > SESSION_EXPIRATION_MS;
};

// Helper to load auth data from storage
const loadAuthFromStorage = (): { token: string | null; user: User | null } => {
	// First check if session has expired
	if (isSessionExpired()) {
		clearAuthFromStorage();
		return { token: null, user: null };
	}

	// Check localStorage first (remember me), then sessionStorage
	let token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
	let userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);

	if (!token) {
		token = sessionStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
		userData = sessionStorage.getItem(STORAGE_KEYS.USER_DATA);
	}

	if (token && userData) {
		try {
			const user = JSON.parse(userData) as User;
			return { token, user };
		} catch {
			// Invalid JSON, clear storage
			clearAuthFromStorage();
		}
	}

	return { token: null, user: null };
};

const useAuthStore = create<AuthState>()(
	devtools(
		(set, get) => ({
			user: null,
			token: null,
			isAuthenticated: false,
			isInitialized: false,

			initializeAuth: () => {
				const { token, user } = loadAuthFromStorage();

				if (token && user) {
					set({
						token,
						user,
						isAuthenticated: true,
						isInitialized: true,
					});
				} else {
					set({ isInitialized: true });
				}
			},

			login: async (
				email: string,
				password: string,
				rememberMe = false
			): Promise<LoginResult> => {
				try {
					const response = await authApi.signIn({
						userName: email,
						password,
					});

					// Validate that the API role is supported in the business portal
					if (!isValidUserRole(response.role)) {
						return {
							success: false,
							error: 'You do not have permission to access this portal.',
						};
					}

					// Create user object from API response
					// response.role is now validated as UserRole, no mapping needed
					const user: User = {
						name: response.name,
						email,
						image: response.image,
						role: response.role,
						passwordChangeRequired: response.passwordChangeRequired,
						phoneVerified: response.phoneVerified,
					};

					// Save to storage (includes login timestamp)
					saveAuthToStorage(response.token, user, rememberMe);

					// Update state
					set({
						token: response.token,
						user,
						isAuthenticated: true,
					});

					return {
						success: true,
						passwordChangeRequired: response.passwordChangeRequired,
						role: response.role,
					};
				} catch (error) {
					const apiError = error as ApiError;

					// Log error for debugging (in development only)
					if (import.meta.env.DEV) {
						// eslint-disable-next-line no-console
						console.error('Login error:', apiError.message, apiError.status);
					}

					return { success: false, error: apiError.message };
				}
			},

			logout: () => {
				clearAuthFromStorage();
				set({
					user: null,
					token: null,
					isAuthenticated: false,
				});
			},

			checkSessionExpiration: () => {
				if (isSessionExpired()) {
					get().logout();
					return true; // Session was expired
				}
				return false; // Session is still valid
			},

			updateUser: (userData: Partial<User>) => {
				const currentUser = get().user;
				if (currentUser) {
					const updatedUser = { ...currentUser, ...userData };
					set({ user: updatedUser });

					// Update storage
					const storage = getStorage();
					storage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(updatedUser));
				}
			},

			refreshSession: (token: string, userData: Partial<User>) => {
				const currentUser = get().user;
				if (currentUser) {
					const updatedUser = { ...currentUser, ...userData };
					set({ token, user: updatedUser });

					// Update storage with new token and user data
					const storage = getStorage();
					storage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
					storage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(updatedUser));
					// Reset login timestamp to extend session
					storage.setItem(STORAGE_KEYS.LOGIN_TIMESTAMP, Date.now().toString());
				}
			},
		}),
		{ name: 'auth-store' }
	)
);

export default useAuthStore;
